import { getDesignVocabSignals } from "@/lib/agent/designBriefReadiness";

const INFORMATIONAL_DESIGN_TOPIC_PATTERN =
  /\b(?:dress|gown|fabric|material|satin|chiffon|crepe|organza|tulle|mikado|velvet|lace|pockets?|matte|breathable|comfort(?:able)?|outdoor|outside|garden|beach|wedding|bridal|bridesmaid|prom|quincea(?:ñ|n)era|gala|formal|sleeves?|straps?|neckline|silhouette|shape|fit|colou?r|shade|length|hemline|train|waist(?:line)?|budget|price|pricing|cost|affordable)\b/i;

/** Comparisons and indecision name real design values without selecting one.
 * Keep this narrow to dress vocabulary plus an explicit alternative shape so
 * unrelated "between X and Y" conversation does not become design intake. */
export function isIndecisiveDesignChoice(message: string): boolean {
  if (getDesignVocabSignals(message).length === 0) return false;
  const betweenChoice =
    /\b(?:torn|undecided|debating|weighing|choos(?:e|ing)|decid(?:e|ing)|can(?:not|'t|’t)\s+decide|not\s+sure|unsure)\b[^.!?]{0,48}\bbetween\b[^.!?]{0,64}\band\b/i.test(
      message
    ) ||
    /^\s*(?:i(?:['’]?m|\s+am)?\s+)?between\b[^.!?]{0,64}\band\b/i.test(message);
  const eitherOrChoice = /\beither\b[^.!?]{0,64}\bor\b/i.test(message);
  const uncertainOrChoice =
    /\b(?:torn|undecided|debating|weighing|can(?:not|'t|’t)\s+decide|not\s+sure|unsure)\b[^.!?]{0,64}\bor\b/i.test(
      message
    );
  return betweenChoice || eitherOrChoice || uncertainOrChoice;
}

export function normalizeDesignPreferenceContractions(message: string): string {
  // Expand only the unambiguous preference form of 'd, not historical "I'd
  // tried". Callers use this for classification and retain the original text.
  return message.replace(
    /\b(i|we)['’]d(?=\s+(?:like|love|prefer)\b)/gi,
    "$1 would"
  );
}

/** True when a clause names design concepts to ask, compare, or learn about
 * rather than committing them to the dress. Polite edit requests directed at
 * the agent remain directives ("Could you add pockets?"). */
export function isInformationalDesignRequest(message: string): boolean {
  const trimmed = normalizeDesignPreferenceContractions(message.trim());
  if (!trimmed) return false;

  const politeEditDirective =
    /^\s*(?:can|could|would|will)\s+(?:you|we)\s+(?:please\s+)?(?:help\s+me\s+)?(?:add|use|include|make|change|switch|replace|remove|keep|give|design|create|build|draft|sketch)\b/i.test(
      trimmed
    );
  const directComparativeEdit =
    /^\s*(?:can|could)\s+(?:the\s+)?(?:dress|gown|look|design|render|preview|image|front|back|sleeves?|straps?|neckline|collar|bodice|waist(?:line)?|skirt|hem(?:line)?|length|train|silhouette|shape|fit|fabric|material|colou?r|shade|coverage)\s+be\s+(?:longer|shorter|higher|lower|fuller|slimmer|tighter|looser|darker|lighter|deeper|wider|narrower)\b/i.test(
      trimmed
    );
  const explicitQuestionDirective =
    politeEditDirective || directComparativeEdit;
  // A shared-subject preference can start with "would" after splitting a
  // mixed turn ("I'm attending a wedding and would love a sage midi dress").
  // That is still a choice, not an inverted question. Keep requests to learn
  // about a design and unresolved alternatives under the informational gate;
  // the other question/comparison checks below remain authoritative as well.
  const affirmativePreference =
    /^would\s+(?:love|like|prefer)\b(?!\s+to\s+(?:know|learn|understand|hear|see|find\s+out)\b)/i.test(
      trimmed
    ) &&
    !/\b(?:or|opinions?|thoughts|advice|guidance|discuss(?:ion)?|versus|vs)\b/i.test(
      trimmed
    );
  const genericDesignQuestion =
    !explicitQuestionDirective &&
    !affirmativePreference &&
    /^\s*(?:can|could|would|should|is|are|do|does|will)\b/i.test(trimmed) &&
    (getDesignVocabSignals(trimmed).length > 0 ||
      INFORMATIONAL_DESIGN_TOPIC_PATTERN.test(trimmed));
  const punctuationShapedDesignQuestion =
    !explicitQuestionDirective &&
    /[?¿]/.test(trimmed) &&
    (getDesignVocabSignals(trimmed).length > 0 ||
      INFORMATIONAL_DESIGN_TOPIC_PATTERN.test(trimmed));

  return (
    isIndecisiveDesignChoice(trimmed) ||
    /^(?:i|we)\s+(?:(?:just|only)\s+)?(?:need|want|would\s+(?:like|love|prefer))\s+to\s+(?:know|understand|learn|find out)\b/i.test(
      trimmed
    ) ||
    /\b(?:options?|choices?|catalog(?:ue)?|examples?|ideas?)\b/i.test(
      trimmed
    ) ||
    /^(?:(?:actually|maybe|perhaps|so|then)[,\s]+)*(?:what|which|why|how|should)\b/i.test(
      trimmed
    ) ||
    /\b(?:compare|contrast|explain|recommend|suggest)\b/i.test(trimmed) ||
    /\b(?:tell|explain)\s+(?:me|us)\b/i.test(trimmed) ||
    /\bhelp\s+(?:(?:me|us)\s+)?(?:choose|decide|pick)\b/i.test(trimmed) ||
    /\bshow\s+(?:me|us)\s+(?:how|what|whether|if)\b/i.test(trimmed) ||
    (!affirmativePreference &&
      /\bwould\b[\s\S]{0,80}\b(?:look|work|suit)\s*(?:better|like)?\b/i.test(
        trimmed
      )) ||
    /^\s*(?:can|could|is|are|do|does)\b[\s\S]{0,100}\b(?:look|work|suit|better|best|mean|differ)\b/i.test(
      trimmed
    ) ||
    /^\s*(?:can|could|would)\s+(?:i|we)\b[\s\S]{0,100}\b(?:use|choose|pick|wear|go\s+with)\b/i.test(
      trimmed
    ) ||
    genericDesignQuestion ||
    punctuationShapedDesignQuestion
  );
}

/** Split a mixed turn at boundaries where a separate question or directive
 * can begin. Keeping this shared prevents scalar and free-text constraint
 * extraction from applying different notions of "the informational clause." */
export function splitDesignIntentClauses(message: string): string[] {
  return message
    .split(
      /(?:[;!\n]+|(?<!\bvs)\.+|(?<=\?)\s+|[\u2014\u2013]+|(?<!anything\s)(?<!nothing\s)(?<!all\s)\b(?:but|then)\b|,\s*(?=(?:show|tell|compare|explain|recommend|suggest|what|which|why|how|would|could|should|can|do|does|is|are|will)\b)|\band\s+(?=(?:show|tell|compare|explain|recommend|suggest|what|which|why|how|would|could|should|can|do|does|is|are|will)\b))/i
    )
    .map(clause => clause.trim())
    .filter(Boolean);
}

/** Return only clauses that state a choice or constraint. This text is safe to
 * feed to deterministic durable-memory extractors; questions and comparisons
 * still remain in conversation for the planner to answer. */
export function committedDesignText(message: string | undefined): string {
  if (!message?.trim()) return "";
  const clauses = splitDesignIntentClauses(message);
  return (clauses.length > 0 ? clauses : [message])
    .filter(clause => !isInformationalDesignRequest(clause))
    .join(". ")
    .trim();
}
